import { FormEvent, lazy, Suspense, useCallback, useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import {
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { type PaymentRequest, type PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { Portal } from 'react-portal';
import { useHistory } from 'react-router-dom';
import { useBeforeUnload, useLockBodyScroll, useToggle } from 'react-use';

import { Button } from '@m/components/ui/button';
import { toaster } from '@m/components/ui/toaster';
import { TextToPayVisitView } from '@m/types/api';
import { asCents } from '@m/utils/asCents';
import { formatGrpcToCurrency } from '@m/utils/grpc/formatGRPCDollar';
import { heapTrackProperties } from '@m/utils/heap';
import { isBool } from '@m/utils/isBool';
import {
  ExpressCheckout,
  PayWithCard,
  TermsAndConditionsFooter,
  VisitInfo,
} from 'apps/pay/components';
import { MemberConversionFlow } from 'apps/pay/components/MemberConversionFlow/MemberConversionFlow';
import { PayHeader } from 'apps/pay/components/PayHeader/PayHeader';
import { SavePaymentCheckbox } from 'apps/pay/components/SavePaymentCheckbox/SavePaymentCheckbox';
import { useClosedVisitRedirect } from 'apps/pay/hooks';
import { PaymentService } from 'apps/pay/services';

const VehicleConfirmModal = lazy(() => import('apps/pay/screens/VehicleConfirmModal'));
const ServiceFeeModal = lazy(() => import('apps/pay/screens/ServiceFeeInfo'));

function VisitLandingPage({
  visit,
  visitCode,
  usePaymentElement,
}: {
  usePaymentElement: boolean;
  visit: TextToPayVisitView;
  visitCode: string;
}) {
  const history = useHistory();

  useClosedVisitRedirect(visit);

  const stripe = useStripe();
  const elements = useElements();

  const [supportsPaymentRequest, setSupportsPaymentRequest] = useState<boolean | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest>();

  const [renderVehicleConfirm, setRenderVehicleConfirm] = useState(false);
  const [showVehicleConfirm, setShowVehicleConfirm] = useState(false);

  const [renderServiceFeeInfo, setRenderServiceFeeInfo] = useState(false);
  const [showServiceFeeInfo, setShowServiceFeeInfo] = useState(false);
  const [locked, toggleLocked] = useToggle(false);
  useLockBodyScroll(locked);

  const [isPaymentElementComplete, setIsPaymentElementComplete] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  const [memberConversionCompleted, setMemberConversionCompleted] = useState(false);
  const [shouldLinkVehicleAndRegisterUser, setShouldLinkVehicleAndRegisterUser] = useState(false);

  // v2: User's choice for save-for-later checkbox (CA opt-in)
  // Default to API's defaultChoice, fallback to false for CA compliance safety
  // (if display=true but defaultChoice missing, safer to require explicit opt-in)
  const [saveForLaterChoice, setSaveForLaterChoice] = useState<boolean>(
    visit.saveForLater?.defaultChoice ?? false,
  );
  const showSaveForLaterCheckbox = visit.saveForLater?.display ?? false;

  const itemizedAmounts = visit?.itemizedAmounts;

  const openVehicleConfirm = useCallback(() => {
    toggleLocked(true);
    setRenderVehicleConfirm(true);
    setShowVehicleConfirm(true);

    heapTrackProperties('memberless-payment/not-my-vehicle:opened', {
      'timestamp(utc)': new Date().toUTCString(),
      timestamp: Date.now(),
      transactionId: visit.id,
      userId: visit.userId,
    });
  }, [toggleLocked, visit.id, visit.userId]);

  const closeVehicleConfirm = useCallback(
    ({ track }: { track: boolean } = { track: true }) => {
      setShowVehicleConfirm(false);
      toggleLocked(false);
      if (track) {
        heapTrackProperties('memberless-payment/not-my-vehicle:closed', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
          transactionId: visit.id,
          userId: visit.userId,
        });
      }
    },
    [toggleLocked, visit.id, visit.userId],
  );

  const closeCompleteVehicleConfirm = useCallback(() => {
    setRenderVehicleConfirm(false);
  }, []);

  const openServiceFeeInfo = useCallback(() => {
    toggleLocked(true);
    setRenderServiceFeeInfo(true);
    setShowServiceFeeInfo(true);
  }, [toggleLocked]);

  const closeServiceFeeInfo = useCallback(() => {
    setShowServiceFeeInfo(false);
    toggleLocked(false);
  }, [toggleLocked]);

  const closeCompleteServiceFeeInfo = useCallback(() => {
    setRenderServiceFeeInfo(false);
  }, []);

  const completeVisit = useCallback(() => {
    history.replace(`/${visitCode}/complete`);
  }, [history, visitCode]);

  const handleCardPayment = useCallback(
    async (
      token: string,
      options: {
        isBrowserSavedCard?: boolean;
        vendorTransactionId?: string;
      } = {},
    ) => {
      try {
        const isBrowserSaved = isBool(options.isBrowserSavedCard)
          ? options.isBrowserSavedCard
          : false;

        const basePayload = {
          visitCode,
          token,
          isBrowserSavedCard: isBrowserSaved,
          saveForLater: showSaveForLaterCheckbox ? saveForLaterChoice : true,
          vendorTransactionId: options.vendorTransactionId,
        };

        const paymentPayload = visit.showMemberConversionFlow
          ? {
              ...basePayload,
              registerUser: shouldLinkVehicleAndRegisterUser,
              linkVehicle: shouldLinkVehicleAndRegisterUser,
            }
          : basePayload;

        await PaymentService.completePayment(paymentPayload);

        heapTrackProperties('memberless-payment/pay(card):complete', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
        });
        completeVisit();
      } catch (error) {
        heapTrackProperties('memberless-payment/pay(card):failed', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
        });
      }
    },
    [
      visitCode,
      visit.showMemberConversionFlow,
      shouldLinkVehicleAndRegisterUser,
      completeVisit,
      showSaveForLaterCheckbox,
      saveForLaterChoice,
    ],
  );

  const handleBrowserPayment = useCallback(
    async (e: PaymentRequestPaymentMethodEvent) => {
      const token = e.paymentMethod.id;
      try {
        const basePayload = {
          visitCode,
          token,
          isBrowserSavedCard: true,
          saveForLater: showSaveForLaterCheckbox ? saveForLaterChoice : true,
        };

        const paymentPayload = visit.showMemberConversionFlow
          ? {
              ...basePayload,
              registerUser: shouldLinkVehicleAndRegisterUser,
              linkVehicle: shouldLinkVehicleAndRegisterUser,
            }
          : basePayload;

        await PaymentService.completePayment(paymentPayload);

        e.complete('success');
        heapTrackProperties('memberless-payment/pay(managed):complete', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
        });
        completeVisit();
      } catch (error) {
        e.complete('fail');
        heapTrackProperties('memberless-payment/pay(managed):failed', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
        });
      }
    },
    [
      completeVisit,
      visitCode,
      visit.showMemberConversionFlow,
      shouldLinkVehicleAndRegisterUser,
      showSaveForLaterCheckbox,
      saveForLaterChoice,
    ],
  );

  const handleBeforeUnload = useCallback(() => {
    if (visit && visit.open) {
      heapTrackProperties('memberless-payment/landing:abandoned', {
        'timestamp(utc)': new Date().toUTCString(),
        timestamp: Date.now(),
      });
    }
    return false;
  }, [visit]);

  useBeforeUnload(handleBeforeUnload);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (visit) {
      // Wrapping in setTimeout so that this event is
      // not triggered if the page is to be redirected
      // to the receipt page
      timeoutId = setTimeout(() => {
        heapTrackProperties('memberless-payment/landing:viewed', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
        });
      }, 300);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [visit]);

  useEffect(() => {
    async function loadPaymentRequest() {
      if (itemizedAmounts && stripe && !paymentRequest) {
        const amount = asCents(itemizedAmounts.totalPriceAmount);
        const parkingCost = asCents(itemizedAmounts.price);

        const displayItems = [
          {
            label: 'Parking cost',
            amount: parkingCost,
          },
        ];

        if (itemizedAmounts.validationAmount) {
          displayItems.push({
            label: 'Validation',
            amount: asCents(itemizedAmounts.validationAmount),
          });
        }

        if (itemizedAmounts.taxAmount) {
          displayItems.push({
            label: 'Tax',
            amount: asCents(itemizedAmounts.taxAmount),
          });
        }

        if (itemizedAmounts.feeAmount) {
          displayItems.push({
            label: 'Service fee',
            amount: asCents(itemizedAmounts.feeAmount),
          });
        }

        const paymentReq = stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'Metropolis Parking',
            amount,
          },
          displayItems,
          requestPayerName: true,
          requestPayerPhone: true,
        });

        try {
          const result = await paymentReq.canMakePayment();

          if (result) {
            setPaymentRequest(paymentReq);
            setSupportsPaymentRequest(true);
          } else {
            setSupportsPaymentRequest(false);
          }
        } catch (e) {
          setSupportsPaymentRequest(false);
        }

        paymentReq.on('paymentmethod', handleBrowserPayment);
      }
    }

    if (!usePaymentElement) loadPaymentRequest();
  }, [handleBrowserPayment, itemizedAmounts, paymentRequest, stripe, usePaymentElement]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmittingPayment(true);

    try {
      const stripeResponse = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/${visitCode}/complete`,
        },
        redirect: 'if_required',
      });

      const { paymentIntent, error } = stripeResponse;

      if (error) {
        toaster.create({
          description: error.message || 'There was an error processing your payment',
          type: 'error',
          duration: 8 * 1000,
        });
        setIsSubmittingPayment(false);
        return;
      }

      await handleCardPayment(paymentIntent.payment_method?.toString() || '', {
        isBrowserSavedCard: true,
        vendorTransactionId: paymentIntent.id,
      });
    } catch (error) {
      toaster.create({
        description: 'An unexpected error occurred while processing your payment',
        type: 'error',
        duration: 8 * 1000,
      });
      setIsSubmittingPayment(false);
    }
  };

  const handleExpressCheckout = async () => {
    if (!stripe || !elements) {
      toaster.create({
        description: 'Payment system is not ready. Please try again.',
        type: 'error',
        duration: 8 * 1000,
      });

      heapTrackProperties('Text to pay express checkout', {
        'timestamp(utc)': new Date().toUTCString(),
        timestamp: Date.now(),
        paymentMethod: 'unknown',
        status: 'failed',
        visitId: visit.id,
        error: 'Payment system not ready',
      });

      return;
    }

    setIsSubmittingPayment(true);

    try {
      const stripeResponse = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/${visitCode}/complete`,
        },
        redirect: 'if_required',
      });

      const { paymentIntent, error } = stripeResponse;

      if (error) {
        toaster.create({
          description: error.message || 'Express checkout payment failed',
          type: 'error',
          duration: 8 * 1000,
        });

        heapTrackProperties('Text to pay express checkout', {
          'timestamp(utc)': new Date().toUTCString(),
          timestamp: Date.now(),
          paymentMethod: 'unknown',
          status: 'failed',
          visitId: visit.id,
          error: error.message || 'Express checkout payment failed',
        });

        setIsSubmittingPayment(false);
        return;
      }

      const paymentMethod =
        typeof paymentIntent.payment_method === 'object' ? paymentIntent.payment_method : null;
      const paymentMethodType = paymentMethod?.type || 'unknown';

      // Track success
      heapTrackProperties('Text to pay express checkout', {
        'timestamp(utc)': new Date().toUTCString(),
        timestamp: Date.now(),
        paymentMethod: paymentMethodType,
        status: 'success',
        visitId: visit.id,
        paymentIntentId: paymentIntent.id,
      });

      await handleCardPayment(paymentIntent.payment_method?.toString() || '', {
        isBrowserSavedCard: true,
        vendorTransactionId: paymentIntent.id,
      });
    } catch (error) {
      toaster.create({
        description: 'An unexpected error occurred during express checkout',
        type: 'error',
        duration: 8 * 1000,
      });
      setIsSubmittingPayment(false);
    }
  };

  const handleMemberConversionContinue = useCallback((linkVehicle: boolean) => {
    setShouldLinkVehicleAndRegisterUser(linkVehicle);
    setMemberConversionCompleted(true);
  }, []);

  const handleBack = useCallback(() => {
    setMemberConversionCompleted(false);
  }, []);

  if (visit.showMemberConversionFlow && !memberConversionCompleted) {
    return (
      <MemberConversionFlow
        vehicle={visit.vehicle}
        onContinue={handleMemberConversionContinue}
        visitId={visit.id}
        userId={visit.userId}
      />
    );
  }

  return (
    <>
      {visit.showMemberConversionFlow ? <PayHeader onBack={handleBack} /> : <PayHeader />}
      <VisitInfo
        visit={visit}
        onClickNotMyVehicle={openVehicleConfirm}
        onClickServiceFeeInfo={openServiceFeeInfo}
      />
      {usePaymentElement ? (
        <Box width="100%" mt={4} px={4}>
          <Box mb={4}>
            <ExpressCheckoutElement
              onConfirm={handleExpressCheckout}
              options={{ buttonHeight: 40 }}
            />
          </Box>
          <form onSubmit={handleSubmit}>
            <PaymentElement onChange={(e) => setIsPaymentElementComplete(e.complete)} />
            <SavePaymentCheckbox
              display={showSaveForLaterCheckbox}
              checked={saveForLaterChoice}
              onChange={setSaveForLaterChoice}
            />
            <Button
              variant="brand"
              type="submit"
              size="2xl"
              width="100%"
              mt={4}
              disabled={!isPaymentElementComplete || isSubmittingPayment}
              loading={isSubmittingPayment}
            >
              {`Pay $${formatGrpcToCurrency(visit?.quote?.totalPrice)}`}
            </Button>
          </form>
        </Box>
      ) : (
        <>
          <ExpressCheckout
            supportsPaymentRequest={supportsPaymentRequest}
            stripePaymentRequest={paymentRequest}
          />
          <PayWithCard
            visit={visit}
            supportsPaymentRequest={supportsPaymentRequest}
            onCardTokenReceived={handleCardPayment}
          />
        </>
      )}

      {/* Global footer for all payment views */}
      <Box px={4} pb={4}>
        <TermsAndConditionsFooter />
      </Box>

      <Suspense fallback={null}>
        {renderVehicleConfirm && (
          <Portal>
            <VehicleConfirmModal
              show={showVehicleConfirm}
              onClose={closeVehicleConfirm}
              onCloseComplete={closeCompleteVehicleConfirm}
            />
          </Portal>
        )}
      </Suspense>
      <Suspense fallback={null}>
        {renderServiceFeeInfo && (
          <Portal>
            <ServiceFeeModal
              show={showServiceFeeInfo}
              onClose={closeServiceFeeInfo}
              onCloseComplete={closeCompleteServiceFeeInfo}
            />
          </Portal>
        )}
      </Suspense>
    </>
  );
}

export default VisitLandingPage;
